import React from 'react';
import type { TFunction } from 'i18next';
import { formatCurrency } from '../../../utils/formatCurrency';

export interface OrderSummaryProps {
  plan: { monthlyPrice: number; originalPrice?: number };
  payroll: { monthlyPrice: number; originalPrice?: number; perEmployee: number } | null | undefined;
  planId: string;
  payrollId: string | null;
  total: number;
  savings: number;
  t: TFunction;
  language: string;
}

export function OrderSummary({ plan, payroll, planId, payrollId, total, savings, t, language }: OrderSummaryProps): React.ReactElement {
  return (
    <div className="rounded-2xl border border-hairline bg-white sticky" style={{ padding: 28, top: 24 }}>
      <h3 className="mb-5 mt-0 font-extrabold text-ink" style={{ fontSize: 18 }}>{t("checkout.orderSummary.title")}</h3>

      <div className="flex items-center justify-between text-body" style={{ padding: "10px 0" }}>
        <div>
          <div className="font-semibold text-ink">{t("plans." + planId + ".name")}</div>
          {plan.originalPrice && plan.originalPrice > plan.monthlyPrice && (
            <div className="text-tiny text-muted line-through">{t("currency.perMonth", { amount: plan.originalPrice })}</div>
          )}
        </div>
        <div className="font-bold text-ink">{t("currency.perMonth", { amount: plan.monthlyPrice })}</div>
      </div>

      {payroll && (
        <div className="flex items-center justify-between text-body border-t" style={{ padding: "10px 0", borderColor: "#F9FAFA" }}>
          <div>
            <div className="font-semibold text-ink">{t("payrollTiers." + payrollId + ".name")}</div>
            {payroll.originalPrice && payroll.originalPrice > payroll.monthlyPrice && (
              <div className="text-tiny text-muted line-through">{t("currency.perMonth", { amount: payroll.originalPrice })}</div>
            )}
            <div className="text-tiny text-sub">{t("currency.perEmployeeMonth", { amount: payroll.perEmployee })}</div>
          </div>
          <div className="font-bold text-ink">{t("currency.perMonth", { amount: payroll.monthlyPrice })}</div>
        </div>
      )}

      <div className="mt-2 flex items-baseline justify-between border-t border-hairline" style={{ padding: "20px 0 12px" }}>
        <div className="text-body font-bold text-ink">{t("checkout.orderSummary.totalDueToday")}</div>
        <div className="font-extrabold text-ink" style={{ fontSize: 24 }}>{formatCurrency(total, language)}</div>
      </div>

      {savings > 0 && (
        <div className="font-bold text-brand" style={{ fontSize: 13.5, padding: "4px 0 12px" }}>{t("checkout.orderSummary.savingsNote", { amount: savings })}</div>
      )}
      <div className="pb-3 text-tiny text-muted">{t("checkout.orderSummary.taxesNote")}</div>
      <div className="pt-3 text-tiny text-sub leading-normal border-t border-dashed border-hairline">
        {t("checkout.orderSummary.footerNote")}
      </div>
    </div>
  );
}
